/**
 * « L'API ne répond plus » traité comme une coupure — pas comme une erreur.
 *
 * PROBLÈME RÉSOLU. Toute la machinerie offline-first existe déjà (file
 * d'écritures, clé d'idempotence, bandeau, reprise automatique), mais elle
 * n'écoute qu'une seule source : `navigator.onLine`, c'est-à-dire le réseau du
 * POSTE. Or la panne la plus fréquente en clinique n'est pas là : c'est l'API
 * qui s'absente — un déploiement recrée le conteneur, et son point d'entrée
 * enchaîne migrations, migrations de chaque base tenant, deux seeds RBAC et
 * collectstatic avant d'ouvrir gunicorn. Pendant ce temps le Wi-Fi est intact,
 * `navigator.onLine` reste `true`, aucun bandeau ne s'affiche, et la file
 * d'écritures ne s'arme pas. L'agent voit « Échec de l'enregistrement ».
 *
 * Le rejeu discret de `client.ts` (~15 s en lecture, ~4 s en écriture) couvre
 * les absences brèves. Au-delà, ce module prend le relais : il bascule
 * l'application en mode hors ligne, exactement comme une coupure réseau.
 *
 * POURQUOI UNE SONDE, ET PAS UNE BASCULE DIRECTE. Un 504 isolé ne prouve pas
 * que le serveur est absent : `proxy_read_timeout` vaut 300 s et un export
 * volumineux peut l'atteindre alors que l'API se porte très bien. Basculer
 * toute la clinique en « hors ligne » sur ce seul signal afficherait un bandeau
 * mensonger et mettrait en file des saisies qui n'avaient aucune raison d'y
 * aller. On demande donc son avis au serveur lui-même, via `/health/` — une
 * requête, une réponse, pas d'ambiguïté.
 *
 * RETOUR À LA NORMALE. Personne n'a à recharger la page : le scrutin rappelle
 * `/health/` avec un intervalle croissant et, au premier succès, rend la main à
 * l'application. TanStack Query rejoue alors les écritures en file, chacune
 * avec sa clé d'idempotence — le serveur dédoublonne ce qui aurait malgré tout
 * abouti avant la coupure.
 */
import axios from "axios";
import { onlineManager } from "@tanstack/react-query";
import { useSyncExternalStore } from "react";

import { env } from "@/config/env";

/** Au-delà, `/health/` est réputée sans réponse. Court volontairement : cette
 *  attente s'ajoute à celle que l'agent subit déjà devant son formulaire. */
const PROBE_TIMEOUT_MS = 3_000;

/** Intervalles du scrutin de retour, en millisecondes. Croissants puis
 *  stables : un redémarrage dure une à deux minutes, inutile de marteler
 *  `/health/` toutes les deux secondes pendant tout ce temps. La dernière
 *  valeur est répétée jusqu'au retour. */
const POLL_DELAYS_MS = [2_000, 4_000, 8_000, 15_000, 30_000] as const;

let unreachable = false;
let pollTimer: ReturnType<typeof setTimeout> | null = null;
let pollStep = 0;
/** Sonde en vol : dix requêtes qui échouent ensemble ne doivent en déclencher
 *  qu'une seule, et toutes attendre le même verdict. */
let inFlight: Promise<boolean> | null = null;

const listeners = new Set<() => void>();

function emit(): void {
  for (const listener of listeners) listener();
}

/** `true` quand le serveur a été constaté injoignable et ne l'est plus démenti. */
export function isServerUnreachable(): boolean {
  return unreachable;
}

/** S'abonner à l'état ; renvoie le désabonnement. */
export function subscribeServerReachability(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Le bandeau distingue les deux causes : « votre poste n'a plus de réseau »
 *  et « le serveur est momentanément absent » n'appellent pas le même geste. */
export function useIsServerUnreachable(): boolean {
  return useSyncExternalStore(subscribeServerReachability, isServerUnreachable, () => false);
}

/** Une requête à `/health/`, hors de notre client axios — ses intercepteurs
 *  rappelleraient ce module, et la sonde s'auto-alimenterait. */
async function probe(): Promise<boolean> {
  try {
    await axios.get(`${env.VITE_API_URL}/health/`, {
      timeout: PROBE_TIMEOUT_MS,
      withCredentials: true,
    });
    return true;
  } catch {
    return false;
  }
}

function stopPolling(): void {
  if (pollTimer !== null) {
    clearTimeout(pollTimer);
    pollTimer = null;
  }
  pollStep = 0;
}

/** Le serveur répond de nouveau : on rend la main. La reprise des écritures en
 *  file est déclenchée par `onlineManager` lui-même (MutationCache.onOnline). */
function markReachable(): void {
  stopPolling();
  if (!unreachable) return;
  unreachable = false;
  onlineManager.setOnline(true);
  emit();
}

function scheduleNextPoll(): void {
  if (pollTimer !== null) return;
  const delay = POLL_DELAYS_MS[Math.min(pollStep, POLL_DELAYS_MS.length - 1)] ?? 30_000;
  pollStep += 1;
  pollTimer = setTimeout(() => {
    pollTimer = null;
    void (async () => {
      // Réseau du poste réellement coupé : `navigator.onLine` fait autorité et
      // l'événement `online` du navigateur relancera tout. Inutile de sonder
      // dans le vide, et surtout de repasser l'application en ligne à sa place.
      if (typeof navigator !== "undefined" && navigator.onLine === false) {
        scheduleNextPoll();
        return;
      }
      if (await probe()) markReachable();
      else scheduleNextPoll();
    })();
  }, delay);
}

/**
 * Demande au serveur s'il est vraiment absent, et bascule l'application hors
 * ligne si c'est le cas. Renvoie `true` quand l'absence est CONFIRMÉE.
 *
 * À appeler quand une requête a épuisé son budget de rejeu, jamais au premier
 * échec : en deçà, `client.ts` masque déjà l'incident sans que personne n'ait à
 * le savoir.
 */
export async function confirmServerUnreachable(): Promise<boolean> {
  // Poste hors réseau : ce n'est pas le serveur qui est en cause, et le
  // bandeau « hors ligne » est déjà affiché par la détection navigateur.
  if (typeof navigator !== "undefined" && navigator.onLine === false) return false;
  if (unreachable) return true;
  if (inFlight) return inFlight;

  inFlight = (async () => {
    try {
      if (await probe()) return false;
      unreachable = true;
      // Bascule le manager de TanStack Query : à partir d'ici, les créations
      // offline-capables sont mises en file au lieu d'échouer, et les requêtes
      // servent le cache. C'est le même état qu'une coupure réseau — d'où la
      // réutilisation du manager plutôt qu'un second mécanisme parallèle.
      onlineManager.setOnline(false);
      emit();
      scheduleNextPoll();
      return true;
    } finally {
      inFlight = null;
    }
  })();
  return inFlight;
}

/**
 * Bouton « Réessayer » du bandeau : sonde tout de suite, sans attendre le
 * prochain tour du scrutin (qui peut être à 30 s). Renvoie `true` si le
 * serveur répond — l'application repasse alors en ligne d'elle-même. Sans
 * réponse, le scrutin continue comme avant.
 */
export async function retryServerNow(): Promise<boolean> {
  if (!unreachable) return true;
  if (await probe()) {
    markReachable();
    return true;
  }
  return false;
}

/** Remise à zéro complète — réservée aux tests. */
export function resetServerReachability(): void {
  stopPolling();
  inFlight = null;
  if (unreachable) {
    unreachable = false;
    onlineManager.setOnline(true);
  }
  listeners.clear();
}
